<template>
  <div id="app">
    <nav class="bg-gray-800 text-white py-6 px-4">
      <div class="mx-auto max-w-[1600px] px-4 sm:px-5 lg:px-6 flex items-center justify-center relative">
        <!-- Dynamic Back Button -->
        <button
          v-if="showBackButton && backButtonConfig"
          @click="handleBackNavigation"
          class="absolute left-0 text-gray-300 hover:text-white hover:bg-gray-700 px-3 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          <span class="text-sm font-medium">{{ backButtonConfig.text }}</span>
        </button>
        
        <!-- Profile Dropdown (only when inside a lab route) -->
        <div v-if="isAuthenticated && $route.params.labId" class="absolute right-0" ref="profileDropdownRef">
          <div class="relative">
            <button
              @click="toggleProfileDropdown"
              class="text-gray-300 hover:text-white hover:bg-gray-700 px-3 py-2 rounded-lg transition-all duration-200 flex items-center"
            >
              <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            </button>
            
            <!-- Dropdown Menu -->
            <div
              v-show="showProfileDropdown"
              class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200"
            >
              <button
                v-if="!isOnUploadChoicePage"
                @click="navigateToUploadChoice"
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <div class="flex items-center space-x-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                  </svg>
                  <span>Upload New Data</span>
                </div>
              </button>
              <button
                v-if="hasDataAvailable"
                @click="navigateToDataDashboard"
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <div class="flex items-center space-x-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                  </svg>
                  <span>Data Dashboard</span>
                </div>
              </button>
              <button
                @click="navigateToCompletedJobs"
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <div class="flex items-center space-x-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                  </svg>
                  <span>Completed Jobs</span>
                </div>
              </button>
              <button
                @click="navigateToBilling"
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <div class="flex items-center space-x-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path>
                  </svg>
                  <span>Billing</span>
                </div>
              </button>
              <button
                @click="navigateToSetLabDefaults"
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <div class="flex items-center space-x-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                  <span>Set Lab Defaults</span>
                </div>
              </button>
              <div class="border-t border-gray-200 my-1"></div>
              <button
                @click="handleLogout"
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <div class="flex items-center space-x-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                  </svg>
                  <span>Logout</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
    
    <main class="mx-auto max-w-[1600px] px-4 sm:px-5 lg:px-6 pt-0 pb-4">
      <router-view />
    </main>

    <BaseModal
      :isVisible="storageWarningVisible"
      title="Cloud storage notice"
      width="md"
      :showCloseButton="true"
      @close="dismissStorageWarning"
    >
      <template #content>
        <p class="text-sm text-gray-700 mb-4">
          Total lab data in cloud storage is about
          <strong>{{ storageWarningGbDisplay }}&nbsp;GB</strong>, which is above the
          {{ storageWarningThresholdGbDisplay }}&nbsp;GB notice threshold. Please delete older completed jobs you no longer need.
        </p>
        <button
          type="button"
          class="text-sm font-medium text-indigo-600 hover:text-indigo-800"
          @click="goToCompletedJobsFromWarning"
        >
          Open Completed Jobs
        </button>
      </template>
    </BaseModal>

  </div>
</template>

<script>
import axios from 'axios'
import BaseModal from './components/BaseModal.vue'
import authService from './services/authService.js'
import { buildApiUrl, API_ENDPOINTS } from './config/api.js'
import { useLabPath } from './composables/useLabPath.js'

export default {
  name: 'App',
  components: {
    BaseModal
  },
  setup() {
    const toLab = useLabPath()
    return { toLab }
  },
  data() {
    return {
      showBackButton: false,
      backButtonConfig: null,
      navigationHistory: [],
      isAuthenticated: false,
      currentUser: null,
      showProfileDropdown: false,
      // Global app state - make it reactive
      globalState: {
        configuredData: null,
        processingResults: null,
        cohortFiles: [],
        cohortProcessingResults: null,
        subid: '',
        datasetId: '',
        sessionId: null
      },
      storageWarningVisible: false,
      storageWarningMb: 0,
      storageWarningThresholdMb: 1024,
      _storageWarningUnhook: null,
      _storageWarningCheckInFlight: false
    }
  },
  computed: {
    isOnUploadChoicePage() {
      return this.$route.name === 'UploadChoice'
    },
    hasDataAvailable() {
      // Check if there's configured data or processing results available
      // Check global state first, then sessionStorage as fallback
      if (this.globalState.configuredData || this.globalState.processingResults) {
        return true
      }
      
      // Fallback: check sessionStorage
      try {
        const configuredData = sessionStorage.getItem('configuredData')
        const processingResults = sessionStorage.getItem('processingResults')
        return !!(configuredData || processingResults)
      } catch (error) {
        return false
      }
    },
    storageWarningGbDisplay() {
      return this.formatMbAsGb(this.storageWarningMb)
    },
    storageWarningThresholdGbDisplay() {
      return this.formatMbAsGb(this.storageWarningThresholdMb)
    }
  },
  provide() {
    return {
      globalState: this.globalState,
      updateGlobalState: this.updateGlobalState,
      updateCurveFeatures: this.updateCurveFeatures,
      clearGlobalState: this.clearGlobalState,
      setBackButton: this.setBackButton,
      hideBackButton: this.hideBackButton
    }
  },
  methods: {
    updateGlobalState(newState) {
      console.log('🔄 App.vue updateGlobalState called with:', newState)
      Object.assign(this.globalState, newState)
      // Sync with sessionStorage for persistence
      this.syncToSessionStorage()
      console.log('✅ App.vue global state updated:', {
        hasConfiguredData: !!this.globalState.configuredData,
        hasProcessingResults: !!this.globalState.processingResults,
        subid: this.globalState.subid,
        datasetId: this.globalState.datasetId,
        sessionId: this.globalState.sessionId
      })
    },

    updateCurveFeatures(curveFeatures) {
      console.log('🔄 App.vue updateCurveFeatures called')
      if (!this.globalState.processingResults) {
        console.warn('⚠️ No processingResults in global state, cannot update curve features')
        return
      }
      
      // Update curve features in global state
      this.globalState.processingResults.curve_features = curveFeatures
      
      // CRITICAL: Also update sessionStorage directly to ensure the latest curve features are persisted
      // This ensures that when components reload from sessionStorage, they get the updated data
      try {
        const storedResults = sessionStorage.getItem('processingResults')
        if (storedResults) {
          const results = JSON.parse(storedResults)
          results.curve_features = curveFeatures
          sessionStorage.setItem('processingResults', JSON.stringify(results))
          console.log('✅ Updated curve features in sessionStorage directly')
        }
      } catch (error) {
        console.warn('Could not update curve features in sessionStorage:', error)
      }
      
      // Sync full global state to sessionStorage
      this.syncToSessionStorage()
      
      console.log('✅ Curve features updated globally:', {
        curveCount: curveFeatures?.length || 0,
        sessionId: this.globalState.processingResults.session_id
      })
      
      // Emit custom event for components that need to react to curve feature changes
      window.dispatchEvent(new CustomEvent('curve-features-updated', {
        detail: { curveFeatures }
      }))
    },
    
    clearGlobalState() {
      console.log('🧹 App.vue clearing global state')
      this.globalState.configuredData = null
      this.globalState.processingResults = null
      this.globalState.subid = ''
      this.globalState.datasetId = ''
      this.globalState.sessionId = null
      this.syncToSessionStorage()
    },
    
    syncToSessionStorage() {
      try {
        if (this.globalState.configuredData) {
          sessionStorage.setItem('configuredData', JSON.stringify(this.globalState.configuredData))
        }
        if (this.globalState.processingResults) {
          sessionStorage.setItem('processingResults', JSON.stringify(this.globalState.processingResults))
        }
      } catch (error) {
        console.warn('Could not sync to sessionStorage:', error)
      }
    },
    
    loadFromSessionStorage() {
      try {
        console.log('🔍 App.vue loading from sessionStorage...')
        const configuredData = sessionStorage.getItem('configuredData')
        if (configuredData) {
          const data = JSON.parse(configuredData)
          this.globalState.configuredData = data
          this.globalState.subid = data.subid || ''
          this.globalState.datasetId = data.dataset_identifier || ''
          console.log('✅ App.vue loaded configuredData to global state:', {
            subid: data.subid,
            datasetId: data.dataset_identifier,
            hasData: !!data.data
          })
        } else {
          console.log('⚠️ App.vue: No configuredData found in sessionStorage')
        }
        
        const processingResults = sessionStorage.getItem('processingResults')
        if (processingResults) {
          this.globalState.processingResults = JSON.parse(processingResults)
          console.log('✅ App.vue loaded processingResults to global state')
        } else {
          console.log('⚠️ App.vue: No processingResults found in sessionStorage')
        }
        
        console.log('🔍 App.vue global state after loading:', {
          hasConfiguredData: !!this.globalState.configuredData,
          hasProcessingResults: !!this.globalState.processingResults,
          subid: this.globalState.subid,
          datasetId: this.globalState.datasetId
        })
      } catch (error) {
        console.warn('Could not load from sessionStorage:', error)
      }
    },
    
    // Navigation methods for back button
    setBackButton(config) {
      this.showBackButton = true
      this.backButtonConfig = config
      console.log('Back button configured:', config)
    },

    hideBackButton() {
      this.showBackButton = false
      this.backButtonConfig = null
      console.log('Back button hidden')
    },

    handleBackNavigation() {
      if (this.backButtonConfig) {
        if (this.backButtonConfig.route) {
          this.$router.push(this.toLab(this.backButtonConfig.route))
        } else if (this.backButtonConfig.callback) {
          this.backButtonConfig.callback()
        }
      }
    },

    // Profile dropdown methods
    toggleProfileDropdown() {
      this.showProfileDropdown = !this.showProfileDropdown
    },
    
    handleClickOutside(event) {
      if (this.$refs.profileDropdownRef && !this.$refs.profileDropdownRef.contains(event.target)) {
        this.showProfileDropdown = false
      }
    },
    
    navigateToCompletedJobs() {
      this.showProfileDropdown = false
      this.$router.push(this.toLab('/completed-jobs'))
    },
    
    navigateToBilling() {
      this.showProfileDropdown = false
      this.$router.push(this.toLab('/billing'))
    },
    
    navigateToSetLabDefaults() {
      this.showProfileDropdown = false
      this.$router.push(this.toLab('/set-lab-defaults'))
    },
    
    navigateToUploadChoice() {
      this.showProfileDropdown = false
      this.$router.push(this.toLab('/'))
    },
    
    navigateToDataDashboard() {
      this.showProfileDropdown = false
      if (this.globalState.configuredData || this.globalState.processingResults) {
        const subid = this.globalState.subid || this.globalState.configuredData?.subid || this.globalState.processingResults?.subid
        const datasetId = this.globalState.datasetId || this.globalState.configuredData?.dataset_identifier || this.globalState.processingResults?.dataset_identifier
        
        if (subid && datasetId) {
          this.$router.push({
            path: this.toLab('/subject-dashboard'),
            query: {
              subid: subid,
              dataset: datasetId
            }
          })
        } else {
          this.$router.push(this.toLab('/subject-dashboard'))
        }
      } else {
        this.$router.push(this.toLab('/'))
      }
    },

    // Authentication methods
    async handleLogout() {
      try {
        this.showProfileDropdown = false
        await authService.logout()
        this.isAuthenticated = false
        this.currentUser = null
        
        // Clear all session data on logout
        this.clearGlobalState()
        try {
          sessionStorage.clear()
          localStorage.removeItem('configuredData')
          localStorage.removeItem('processingResults')
          localStorage.removeItem('cohortFiles')
          localStorage.removeItem('cohortProcessingResults')
          console.log('Storage cleared on logout')
        } catch (error) {
          console.warn('Could not clear storage on logout:', error)
        }
        
        this.$router.push({ name: 'Login' })
      } catch (error) {
        console.error('Logout error:', error)
      }
    },

    onAuthStateChange(isAuthenticated, user) {
      const wasAuthenticated = this.isAuthenticated
      this.isAuthenticated = isAuthenticated
      this.currentUser = user
      // Session restored (e.g. page reload with token): still run one storage-usage check per login session
      if (isAuthenticated && !wasAuthenticated) {
        try {
          if (!sessionStorage.getItem('sdm_storage_warning_dismissed')) {
            sessionStorage.setItem('sdm_pending_storage_check', '1')
          }
        } catch (_) { /* ignore */ }
        this.$nextTick(() => this.maybeShowProcessedStorageWarning())
      }
    },
    
    addCleanupOnUnload() {
      // Track if page is being closed vs refreshed/navigated within app
      // Only cleanup when tab/browser is actually closed, not on refresh or navigation
      
      let userNavigated = false // Track if user clicked a link or submitted a form
      
      // Track explicit navigation (link clicks, form submits)
      document.addEventListener('click', (event) => {
        const target = event.target.closest('a, button[type="submit"], form')
        if (target && (target.tagName === 'A' || target.tagName === 'FORM' || target.type === 'submit')) {
          userNavigated = true
        }
      }, true) // Use capture phase
      
      // Track form submissions
      document.addEventListener('submit', () => {
        userNavigated = true
      }, true)
      
      // Use pagehide event - persisted=false means page is being unloaded (close or refresh)
      // persisted=true means page is being cached (back/forward navigation)
      window.addEventListener('pagehide', (event) => {
        // Only cleanup if:
        // 1. Page is NOT being persisted (not a back/forward navigation)
        // 2. User did NOT explicitly navigate (no link click or form submit)
        // 3. User is authenticated
        // Note: We can't perfectly detect refresh vs close, but we avoid cleanup
        // on explicit navigation within the app
        if (!event.persisted && !userNavigated && this.isAuthenticated) {
          this.cleanupBeforeUnload()
        }
        // Reset flag for next time
        userNavigated = false
      })
    },
    
    async cleanupBeforeUnload() {
      // Only cleanup if user is authenticated
      if (!this.isAuthenticated) return
      
      // Get session token
      const sessionToken = localStorage.getItem('session_token')
      if (!sessionToken) return
      
      // Send cleanup request using navigator.sendBeacon (more reliable for page unload)
      try {
        const cleanupUrl = buildApiUrl('/api/process/cleanup')
        const cleanupData = JSON.stringify({ session_token: sessionToken })
        
        // Try sendBeacon first (more reliable)
        if (navigator.sendBeacon) {
          const blob = new Blob([cleanupData], { type: 'application/json' })
          navigator.sendBeacon(cleanupUrl, blob)
          console.log('✅ S3 cleanup request sent via sendBeacon (tab closed)')
        } else {
          // Fallback to synchronous XHR (less reliable but better than nothing)
          const xhr = new XMLHttpRequest()
          xhr.open('POST', cleanupUrl, false) // Synchronous
          xhr.setRequestHeader('Content-Type', 'application/json')
          xhr.send(cleanupData)
          console.log('✅ S3 cleanup request sent via sync XHR (tab closed)')
        }
      } catch (error) {
        console.warn('Cleanup request failed on page unload:', error)
      }
    },

    formatMbAsGb(mb) {
      const gb = Number(mb) / 1024
      if (!Number.isFinite(gb)) return '0'
      return Number(gb.toFixed(2)).toString()
    },

    dismissStorageWarning() {
      this.storageWarningVisible = false
      try {
        sessionStorage.setItem('sdm_storage_warning_dismissed', '1')
      } catch (_) { /* ignore */ }
    },

    goToCompletedJobsFromWarning() {
      this.dismissStorageWarning()
      this.$router.push(this.toLab('/completed-jobs'))
    },

    async maybeShowProcessedStorageWarning() {
      try {
        if (sessionStorage.getItem('sdm_storage_warning_dismissed')) return
        if (!sessionStorage.getItem('sdm_pending_storage_check')) return
        const token = localStorage.getItem('session_token')
        if (!token) return
        if (this._storageWarningCheckInFlight) return
        this._storageWarningCheckInFlight = true
        const { data } = await axios.get(buildApiUrl(API_ENDPOINTS.STORAGE_PROCESSED_RESULTS_USAGE), {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        })
        // Only clear after a successful HTTP response so a failed check can retry on next navigation
        sessionStorage.removeItem('sdm_pending_storage_check')
        if (data.success && data.data?.over_threshold) {
          const d = data.data
          this.storageWarningMb = d.total_mb ?? d.results_mb ?? 0
          this.storageWarningThresholdMb = d.threshold_mb ?? 1024
          this.storageWarningVisible = true
        }
      } catch (err) {
        console.warn('Storage usage check failed:', err)
      } finally {
        this._storageWarningCheckInFlight = false
      }
    }
  },
  
  mounted() {
    // Load global state from sessionStorage on app start
    this.loadFromSessionStorage()
    
    // Initialize authentication
    authService.addAuthListener(this.onAuthStateChange)
    // Skip auth status check on SSO callback — we don't have a session yet; the callback will set it.
    // Match by name or path: /auth/callback (root), /maptac/:labId/auth/callback (legacy Duo URI).
    const isCallbackRoute = this.$route.name === 'SSOCallback' || this.$route.name === 'SSOCallbackMaptac' || this.$route.name === 'SSOCallbackLab' || this.$route.path.endsWith('/auth/callback')
    if (!isCallbackRoute) {
      authService.checkAuthStatus()
    }
    
    // Add cleanup handler for browser close/tab close
    this.addCleanupOnUnload()
    
    // Add click outside handler for profile dropdown
    document.addEventListener('click', this.handleClickOutside)

    this._storageWarningUnhook = this.$router.afterEach(() => {
      this.$nextTick(() => this.maybeShowProcessedStorageWarning())
    })
    this.$nextTick(() => this.maybeShowProcessedStorageWarning())
  },

  beforeUnmount() {
    // Clean up authentication listener
    authService.removeAuthListener(this.onAuthStateChange)
    
    // Remove click outside handler
    document.removeEventListener('click', this.handleClickOutside)

    if (typeof this._storageWarningUnhook === 'function') {
      this._storageWarningUnhook()
    }
  }
}
</script>

<style>
#app {
  min-height: 100vh;
  background-color: #e5e7eb;
}
</style>
